import CategorizedNomenclatureListPage from "../../../common/nomenclatures/CategorizedNomenclatureListPage";
import { ICategorizedNomenclatureEndpoints } from "../../../common/nomenclatures/models/categorizedNomenclatureEndpoints";
import {
  IInventoryItemInputModel,
  IInventoryCategoryFormModel,
  IInventoryCategoryModel,
  inventoryItemSchema,
  getInventoryItemDefaults,
} from "./models/inventoryNomenclatureModels";
import { InventoryNomenclatureFormFields } from "./components/InventoryNomenclatureFormFields";
import { InventoryCategoryFormFields } from "./components/InventoryCategoryFormFields";
import { InventoryNomenclatureItem } from "./components/InventoryNomenclatureItem";
import { InventoryAdjustQuantityDialog } from "./components/InventoryAdjustQuantityDialog";
import { useInventoryNomenclaturePage } from "./hooks/useInventoryNomenclaturePage";
import { InventoryItemActions } from "./components/InventoryItemActions";
import { OnboardingItemKey } from "../../../common/enums";
import { usePageGuide } from "../../onboarding/hooks/usePageGuide";

/**
 * "Продукти" administration page under the new "Инвентар" sidebar dropdown.
 * Reuses the categorized nomenclature infrastructure with:
 *   - a custom category form (emoji picker required)
 *   - item rows showing the category emoji + current qty (low-stock flagged)
 *   - per-row Notes, History, and (managers+) manual quantity adjust actions.
 *
 * All logic lives in useInventoryNomenclaturePage; this component is pure JSX.
 */
export const InventoryNomenclature = () => {
  const {
    endpoints,
    categories,
    activeCategories,
    studioSettings,
    canAdjust,
    customCategoryConfig,
    adjustDialog,
    openAdjustDialog,
    closeAdjustDialog,
    transformPayload,
  } = useInventoryNomenclaturePage();

  usePageGuide(OnboardingItemKey.InventoryGuide);

  return (
    <>
      <CategorizedNomenclatureListPage<
        IInventoryItemInputModel,
        IInventoryItemInputModel,
        IInventoryCategoryModel,
        IInventoryCategoryFormModel
      >
        endpoints={endpoints as unknown as ICategorizedNomenclatureEndpoints}
        title="Продукти"
        description="Тук се намират всички продукти, които продаваш в обекта си. Започни като добавиш категория и след това продукти към нея."
        dialogAddTitle="Добавяне на продукт"
        dialogEditTitle="Редактиране на продукт"
        emptyListText="В момента нямате добавени продукти"
        editOnRowClick
        itemTemplate={(
          item,
          _index,
          isLoading,
          _actionButtons,
          className,
          onEditItem,
        ) => {
          const typed = item as IInventoryItemInputModel | undefined;

          return (
            <InventoryNomenclatureItem
              item={typed}
              isLoading={isLoading}
              className={className}
              extraActions={
                typed?.id ? (
                  <InventoryItemActions
                    item={typed}
                    endpoints={endpoints}
                    studioSettings={studioSettings}
                    canAdjust={canAdjust}
                    onAdjust={openAdjustDialog}
                    onEdit={onEditItem}
                  />
                ) : null
              }
            />
          );
        }}
        formSchema={inventoryItemSchema}
        defaultValues={getInventoryItemDefaults}
        formFields={
          <InventoryNomenclatureFormFields categories={activeCategories} />
        }
        customCategoryConfig={{
          ...customCategoryConfig,
          formFields: <InventoryCategoryFormFields />,
        }}
        transformPayload={transformPayload}
        enableSearch
        uncategorizedEmoji="📦"
      />

      <InventoryAdjustQuantityDialog
        visible={!!adjustDialog}
        onHide={closeAdjustDialog}
        itemId={adjustDialog?.itemId ?? 0}
        itemName={adjustDialog?.itemName ?? ""}
        currentQuantity={adjustDialog?.currentQuantity ?? 0}
      />
    </>
  );
};
